import { MbscCalendarEvent } from '@mobiscroll/react';
import { DateTime } from 'luxon';

import {
  DispatchTripRead,
  RunBambiRunReassignedTripRead,
  RunBambiRunTripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { reassignableTripStatuses } from 'src/common/tripStatuses';
import { formatFullName } from 'src/common/util/formatFullName';

import { getTripCalendarTheme } from '../eventRenderers/TripCalendarEvent/getTripCalendarTheme';
import { getCalendarEventStartAndEndTime } from './getCalendarEventStartAndEndTime';

export function createEventsFromTrips(
  trips:
    | DispatchTripRead[]
    | RunBambiRunTripRead[]
    | RunBambiRunReassignedTripRead[]
): MbscCalendarEvent[] {
  return (
    trips
      .map((trip) => {
        const tripEventTheme = getTripCalendarTheme(trip);
        const resource =
          'assignment_id' in trip ? trip.assignment_id : trip.assignment.id;

        const { start, end } = getCalendarEventStartAndEndTime(trip);

        const event: MbscCalendarEvent = {
          id: trip.id,
          start,
          end,
          title: formatFullName(trip.passenger),
          resource,
          trip,
          bufferBefore:
            'travel_time_minutes' in trip &&
            typeof trip.travel_time_minutes === 'number'
              ? trip.travel_time_minutes
              : undefined,
          // This will be used for buffer color. Because we're using renderScheduleEvent
          // it has no effect on the actual event color, which is handled by the renderScheduleEvent handler
          // Technically, we only show a buffer if the trip is an active status which is always green
          // but just in case we change that in the future, we'll use the theme here
          // If we need different colors that border, let's add a
          // bufferColor to the theme object
          // Mobiscroll sets an opacity on the buffer element, so we may need darker colors
          // The Active border color looks good though
          color: tripEventTheme.border,
        };

        if (!reassignableTripStatuses.includes(trip.status)) {
          event.dragBetweenResources = false;
          event.dragInTime = false;
          event.dragBetweenSlots = false;
        }

        return event;
      })
      // We do our own presorting and then assign an order member to each event to override mobiscroll's default sorting
      // Mobiscroll also uses bufferBefore in sorting, so events with buffers that occur before another trip's start time will
      // appear first even though they have the same or later start time
      .sort((a, b) => {
        const aStartOnlyHoursMinutes =
          getMobiscrollStartTimeWithMinutesAndSecondsOnly(
            a.start as string,
            a.bufferBefore
          );
        const bStartOnlyHoursMinutes =
          getMobiscrollStartTimeWithMinutesAndSecondsOnly(
            b.start as string,
            b.bufferBefore
          );

        if (
          aStartOnlyHoursMinutes.toMillis() !==
          bStartOnlyHoursMinutes.toMillis()
        ) {
          return aStartOnlyHoursMinutes < bStartOnlyHoursMinutes ? -1 : 1;
        }

        // Do the same with end time
        const aEndOnlyHoursMinutes = DateTime.fromISO(a.end).set({
          second: 0,
          millisecond: 0,
        });
        const bEndOnlyHoursMinutes = DateTime.fromISO(b.end).set({
          second: 0,
          millisecond: 0,
        });
        if (
          aEndOnlyHoursMinutes.toMillis() !== bEndOnlyHoursMinutes.toMillis()
        ) {
          return aEndOnlyHoursMinutes < bEndOnlyHoursMinutes ? -1 : 1;
        }

        // If the start and end times are the same, sort by passenger name as mobiscroll does today
        return a.title.localeCompare(b.title);
      })
      .map((event, index) => ({ ...event, order: index }))
  );
}

function getMobiscrollStartTimeWithMinutesAndSecondsOnly(
  start: string,
  bufferBefore?: number
) {
  return DateTime.fromISO(start)
    .set({
      second: 0,
      millisecond: 0,
    })
    .minus({
      // Mobiscroll considers buffer times when calculating event start/end times so we need to match that
      // Not matching causes some events to have a width of 0%// It's also important that Mobiscroll considers buffer times when calculating event start/end
      minutes: typeof bufferBefore === 'number' ? bufferBefore : 0,
    });
}
